import { useApi } from "../../../hooks/useApi";
import { useCallback } from "react";
import { urlReplacer } from "../../../utils";
import moment from "moment";
import { ApiRoutes } from "../../../constants/apiRoutes";
import { UserRole } from "../enums/UserRole";
import { DATE_FORMATS } from "../../../constants";

export default function () {
  const { request } = useApi();

  return {
    fetchAll: useCallback(
      async (role) => {
        return request({
          method: "GET",
          url: `${ApiRoutes.USERS}?role=${role}`,
        });
      },
      [request],
    ),

    fetchOne: useCallback(
      async (id) => {
        return request({
          method: "GET",
          url: `${urlReplacer(ApiRoutes.GET_USER, {
            id,
          })}`,
        });
      },
      [request],
    ),

    fetchCreate: useCallback(
      async ({ ...data }) => {
        return request({
          data,
          method: "POST",
          url: ApiRoutes.CREATE_USER,
        });
      },
      [request],
    ),

    fetchUpdateProfile: useCallback(
      async (id, data) => {
        return request({
          method: "PUT",
          data,
          url: urlReplacer(ApiRoutes.UPDATE_USER_ACCOUNT, { id }),
        });
      },
      [request],
    ),

    fetchDelete: useCallback(
      async (id) => {
        return request({
          method: "DELETE",
          url: `${urlReplacer(ApiRoutes.DELETE_USER, { id })}`,
        });
      },
      [request],
    ),

    fetchVerify: useCallback(
      async (id) => {
        return request({
          method: "POST",
          url: `${urlReplacer(ApiRoutes.VERIFY_USER, { id })}`,
        });
      },
      [request],
    ),

    fetchActivate: useCallback(
      async (code) => {
        return request({
          method: "PUT",
          url: `${ApiRoutes.VERIFY}?code=${code}`,
        });
      },
      [request],
    ),

    fetchEmailPreferences: useCallback(
      async (id: string) => {
        return request({
          method: "GET",
          url: `${urlReplacer(ApiRoutes.USER_EMAIL_PREFERENCES, { id })}`,
          shouldHandleError: true,
        });
      },
      [request],
    ),

    updateEmailPreferences: useCallback(
      async (id: string, data: { type: string }[]) => {
        return request({
          method: "PUT",
          url: `${urlReplacer(ApiRoutes.USER_EMAIL_PREFERENCES, { id })}`,
          data: { unsubscriptions: data },
          shouldHandleError: true,
        });
      },
      [request],
    ),

    fetchTokenEmailPreferences: useCallback(
      async (token) => {
        return request({
          method: "GET",
          url: `${ApiRoutes.USER_TOKEN_EMAIL_PREFERENCES}?token=${token}`,
          shouldHandleError: true,
        });
      },
      [request],
    ),

    updateTokenEmailPreferences: useCallback(
      async (token, data) => {
        return request({
          method: "PUT",
          url: `${ApiRoutes.USER_TOKEN_EMAIL_PREFERENCES}?token=${token}`,
          data: { unsubscriptions: data },
          shouldHandleError: true,
        });
      },
      [request],
    ),

    fetchUnVerify: useCallback(
      async (id) => {
        return request({
          method: "DELETE",
          url: `${urlReplacer(ApiRoutes.VERIFY_USER, { id })}`,
        });
      },
      [request],
    ),

    fetchVerifyProfile: useCallback(
      async (id) => {
        return request({
          method: "POST",
          url: `${urlReplacer(ApiRoutes.VERIFY_PROFILE, { id })}`,
        });
      },
      [request],
    ),

    fetchUnVerifyProfile: useCallback(
      async (id) => {
        return request({
          method: "DELETE",
          url: `${urlReplacer(ApiRoutes.VERIFY_PROFILE, { id })}`,
        });
      },
      [request],
    ),

    fetchStudents: useCallback(async () => {
      return request({
        method: "GET",
        url: `${ApiRoutes.STUDENTS}`,
      });
    }, [request]),

    fetchAgentsStudents: useCallback(
      async (id) => {
        return request({
          method: "GET",
          url: `${urlReplacer(ApiRoutes.MANAGED_STUDENTS, { id })}`,
        });
      },
      [request],
    ),

    fetchCreateStudent: useCallback(
      async (data) => {
        return request({
          data: {
            ...data,
            studentProfile: {
              ...data.studentProfile,
              birth_date: moment(data.studentProfile.birth_date).format(
                DATE_FORMATS.backEnd,
              ),
              country:
                data.studentProfile &&
                data.studentProfile.country &&
                data.studentProfile.country.code,
              citizenship:
                data.studentProfile &&
                data.studentProfile.citizenship &&
                data.studentProfile.citizenship.code,
            },
          },
          method: "POST",
          url: ApiRoutes.CREATE_STUDENT,
        });
      },
      [request],
    ),

    createSupport: useCallback(
      async (data) => {
        return request({
          data,
          method: "POST",
          url: ApiRoutes.CREATE_SUPPORT,
        });
      },
      [request],
    ),

    uploadUserPhoto: useCallback(
      (id, file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: urlReplacer(ApiRoutes.USER_PHOTO_UPLOAD, { id }),
          data: formData,
        });
      },
      [request],
    ),

    uploadMyAccountPhoto: useCallback(
      (file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: `${ApiRoutes.MY_ACCOUNT_PHOTO_UPLOAD}`,
          data: formData,
        });
      },
      [request],
    ),

    uploadUserFile: useCallback(
      (id, file, field) => {
        let formData = new FormData();
        formData.append("file", file);
        formData.append("field", field);
        return request({
          method: "POST",
          url: urlReplacer(ApiRoutes.USER_ACCOUNT_DOCUMENT, { id }),
          data: formData,
        });
      },
      [request],
    ),

    uploadMyAccountFile: useCallback(
      (file, field) => {
        let formData = new FormData();
        formData.append("file", file);
        formData.append("field", field);
        return request({
          method: "POST",
          url: `${ApiRoutes.MY_ACCOUNT_DOCUMENT}`,
          data: formData,
        });
      },
      [request],
    ),

    fetchDeleteMyAccountFile: useCallback(
      async (field) => {
        return request({
          method: "DELETE",
          url: `${ApiRoutes.MY_ACCOUNT_DOCUMENT}?field=${field}`,
        });
      },
      [request],
    ),

    fetchDeleteUserAccountFile: useCallback(
      async (id, field) => {
        return request({
          method: "DELETE",
          url: `${urlReplacer(ApiRoutes.USER_ACCOUNT_DOCUMENT, {
            id,
          })}?field=${field}`,
        });
      },
      [request],
    ),

    uploadDocument: useCallback(
      (file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: `${ApiRoutes.UPLOAD_DOC}`,
          data: formData,
        });
      },
      [request],
    ),

    uploadApplication: useCallback(
      (file) => {
        let formData = new FormData();
        formData.append("file", file);
        return request({
          method: "POST",
          url: `${ApiRoutes.UPLOAD_APPLICATION}`,
          data: formData,
        });
      },
      [request],
    ),

    fetchUserProfile: useCallback(
      (id) => {
        return request({
          method: "GET",
          url: urlReplacer(ApiRoutes.USER_PROFILE, { id }),
        });
      },
      [request],
    ),

    fetchUserApplications: useCallback(
      (id) => {
        return request({
          method: "GET",
          url: urlReplacer(ApiRoutes.USER_APPLICATIONS, { id }),
        });
      },
      [request],
    ),

    fetchMyAccountProfile: useCallback(() => {
      return request({
        method: "GET",
        url: `${ApiRoutes.MY_PROFILE}`,
      });
    }, [request]),

    fetchUpdateMyProfile: useCallback(
      (data) => {
        return request({
          method: "PUT",
          url: ApiRoutes.MY_ACCOUNT,
          data,
        });
      },
      [request],
    ),

    fetchAssignedBranches: useCallback(
      (id) => {
        return request({
          method: "GET",
          url: urlReplacer(ApiRoutes.USER_BRANCHES, { id }),
        });
      },
      [request],
    ),

    fetchAssignedDegrees: useCallback(
      (id) => {
        return request({
          method: "GET",
          url: urlReplacer(ApiRoutes.USER_DEGREES, { id }),
        });
      },
      [request],
    ),

    fetchAssignToSchool: useCallback(
      (userId, data) => {
        return request({
          method: "PUT",
          url: urlReplacer(ApiRoutes.ASSIGN_USER_TO_SCHOOL, {
            userId,
            schoolId: data.school.id,
          }),
        });
      },
      [request],
    ),

    fetchUpdateSchoolRole: useCallback(
      (id, data) => {
        return request({
          method: "PUT",
          url: urlReplacer(ApiRoutes.USER_SCHOOL_ROLE, { id }),
          data: {
            role: data.role.id,
            branches:
              data.role.id === 1
                ? data.branches.map((item) => ({ id: item.id }))
                : undefined,
            degrees:
              data.role.id === 1
                ? data.degrees.map((item) => ({ id: item.id }))
                : undefined,
          },
        });
      },
      [request],
    ),

    saveMyExams: useCallback(
      (data) => {
        return request({
          method: "PUT",
          url: ApiRoutes.MY_PROFILE_EXAMS,
          data: {
            exams:
              data.exams && data.exams.length
                ? data.exams.map((item) => ({
                    examId: item.exam.id,
                    value: item.value,
                  }))
                : null,
          },
        });
      },
      [request],
    ),

    saveUserExams: useCallback(
      (id, data) => {
        return request({
          method: "PUT",
          url: urlReplacer(ApiRoutes.USER_PROFILE_EXAMS, { id }),
          data: {
            exams:
              data.exams && data.exams.length
                ? data.exams.map((item) => ({
                    examId: item.exam.id,
                    value: item.value,
                  }))
                : null,
          },
        });
      },
      [request],
    ),

    fetchStudentCountries: useCallback(() => {
      return request({
        method: "GET",
        url: urlReplacer(ApiRoutes.GET_FILTERED_COUNTRIES, {
          userType: UserRole.Student,
          onlyForVerifiedUsers: false,
        }),
      });
    }, [request]),

    changePassword: useCallback(
      (data) => {
        return request({
          method: "PUT",
          url: ApiRoutes.CHANGE_PASSWORD,
          data,
        });
      },
      [request],
    ),

    resetPassword: useCallback(
      ({ email }) => {
        return request({
          method: "POST",
          url: ApiRoutes.FORGOT_PASSWORD,
          data: { email },
        });
      },
      [request],
    ),

    fetchSchoolCountries: useCallback(() => {
      return request({
        method: "GET",
        url: urlReplacer(ApiRoutes.GET_FILTERED_COUNTRIES, {
          userType: "school",
          onlyForVerifiedUsers: false,
        }),
      });
    }, [request]),
  };
}
